// Product page gallery (desktop only). The main image is split into three zones:
//   left 30%   -> thin "<" indicator, click shows the previous image
//   middle 40% -> thin "+" indicator, click opens the lightbox ON THE IMAGE SHOWN
//   right 30%  -> thin ">" indicator, click shows the next image
// The indicator follows the mouse (the native cursor is hidden over the image). A DOM
// element is used instead of a CSS url() cursor because some browsers / embedded
// webviews ignore custom image cursors.
// Navigation reuses the theme's own thumbnail buttons, so the active thumbnail,
// variant-image hiding and gallery state stay in sync. Wraps around at the ends.
// In this theme each slide has its own lightbox button (.product__media-toggle,
// data-media-id) layered over the image as a sibling in the media list. After slides
// are reordered the top-most button may belong to another image, so a middle click is
// re-routed to the button whose data-media-id matches the active slide.
(() => {
  const desktop = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');
  const EDGE = 0.3; // width share of each arrow zone

  const ICONS = {
    prev: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 4 L7 12 L15 20" /></svg>',
    next: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 4 L17 12 L9 20" /></svg>',
    zoom: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4 V20 M4 12 H20" /></svg>',
  };

  let badge = null;
  let badgeZone = null;
  let rerouting = false;

  function getBadge() {
    if (badge) return badge;
    badge = document.createElement('div');
    badge.className = 'pgn-cursor';
    badge.setAttribute('aria-hidden', 'true');
    document.body.appendChild(badge);
    return badge;
  }

  function showBadge(zone, x, y) {
    const el = getBadge();
    if (zone !== badgeZone) {
      el.innerHTML = ICONS[zone];
      badgeZone = zone;
    }
    el.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)`;
    el.classList.add('is-visible');
  }

  function hideBadge() {
    if (badge) badge.classList.remove('is-visible');
  }

  const visible = (el) => el && getComputedStyle(el).display !== 'none';

  function getThumbs(gallery) {
    return [...gallery.querySelectorAll('.thumbnail-list__item')]
      .filter(visible)
      .map((li) => li.querySelector('button.thumbnail'))
      .filter(Boolean);
  }

  function getItems(gallery) {
    return [...gallery.querySelectorAll('.product__media-list .product__media-item')].filter(visible);
  }

  function activeItem(gallery) {
    return gallery.querySelector('.product__media-list .product__media-item.is-active');
  }

  // Numeric media id of the active slide (slides carry "<section>-<mediaId>").
  function activeMediaId(gallery) {
    const item = activeItem(gallery);
    const raw = item && item.dataset.mediaId;
    return raw ? raw.split('-').pop() : null;
  }

  function isImage(gallery) {
    const item = activeItem(gallery);
    return !!(item && item.querySelector('.media-type-image'));
  }

  function hasMany(gallery) {
    return getThumbs(gallery).length > 1 || getItems(gallery).length > 1;
  }

  // Returns 'prev', 'next', 'zoom', or null when not over the main image.
  function zoneAt(gallery, event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !target.closest('.product__media-list')) return null;
    if (!isImage(gallery)) return null;
    const item = activeItem(gallery);
    const r = item.getBoundingClientRect();
    const { clientX: x, clientY: y } = event;
    if (x < r.left || x > r.right || y < r.top || y > r.bottom) return null;
    if (hasMany(gallery)) {
      if (x < r.left + r.width * EDGE) return 'prev';
      if (x > r.right - r.width * EDGE) return 'next';
    }
    return 'zoom';
  }

  function go(gallery, step) {
    const thumbs = getThumbs(gallery);
    if (thumbs.length > 1) {
      let idx = thumbs.findIndex((b) => b.getAttribute('aria-current') === 'true');
      if (idx < 0) idx = 0;
      thumbs[(idx + step + thumbs.length) % thumbs.length].click();
      return;
    }
    const items = getItems(gallery);
    if (items.length < 2 || typeof gallery.setActiveMedia !== 'function') return;
    let idx = items.indexOf(activeItem(gallery));
    if (idx < 0) idx = 0;
    const next = items[(idx + step + items.length) % items.length];
    const id = next.dataset.mediaId || next.id.replace(/^Slide-/, '');
    gallery.setActiveMedia(id, false);
  }

  // Open the lightbox on the image currently shown.
  function zoomActive(gallery, clickedToggle) {
    const id = activeMediaId(gallery);
    if (!id) return false;
    const toggle = gallery.querySelector(`.product__media-list .product__media-toggle[data-media-id="${id}"]`);
    if (!toggle || toggle === clickedToggle) return false; // already correct: let it through
    rerouting = true;
    try {
      toggle.click();
    } finally {
      rerouting = false;
    }
    return true;
  }

  function setZone(gallery, zone, event) {
    gallery.classList.toggle('pgn-active', !!zone);
    gallery.dataset.pgnZone = zone || '';
    if (zone && event) showBadge(zone, event.clientX, event.clientY);
    else hideBadge();
  }

  function init(gallery) {
    if (gallery.dataset.pgnReady) return;
    gallery.dataset.pgnReady = '1';
    gallery.classList.add('pgn-enabled');

    gallery.addEventListener('pointermove', (event) => {
      if (!desktop.matches || event.pointerType === 'touch') return setZone(gallery, null);
      setZone(gallery, zoneAt(gallery, event), event);
    });

    gallery.addEventListener('pointerleave', () => setZone(gallery, null));
    window.addEventListener('scroll', () => setZone(gallery, null), { passive: true });

    // Capture phase so the theme's lightbox buttons never see arrow-zone clicks, and
    // so a middle click can be re-routed to the active image's own lightbox button.
    gallery.addEventListener(
      'click',
      (event) => {
        if (rerouting || !desktop.matches) return;
        const zone = zoneAt(gallery, event);
        if (!zone) return;
        if (zone === 'zoom') {
          hideBadge();
          const clickedToggle = event.target instanceof Element ? event.target.closest('.product__media-toggle') : null;
          if (zoomActive(gallery, clickedToggle)) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
          return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        go(gallery, zone === 'prev' ? -1 : 1);
      },
      true
    );
  }

  function initAll() {
    document.querySelectorAll('product-info[id^="MainProduct-"] media-gallery').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  // The theme re-renders the product section when a variant changes.
  new MutationObserver(initAll).observe(document.body, { childList: true, subtree: true });
})();
